import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Zap, Star, Navigation, Clock, ShieldCheck, MapPin, Volume2, VolumeX } from "lucide-react";
import { api, apiError, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLiveRefresh } from "@/lib/expressSocket";
import PushOptIn from "./PushOptIn";
import { Button } from "@/components/ui/button";

const MUTE_KEY = "express_alert_muted";

// Alerta sonoro curto + vibração (respeita silêncio do usuário e permissões do navegador).
function alertNewCall() {
  if (localStorage.getItem(MUTE_KEY) === "1") return;
  try { navigator.vibrate?.([200, 100, 200]); } catch { /* noop */ }
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [0, 0.22].forEach((t) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.frequency.value = 880; g.gain.setValueAtTime(0.0001, ctx.currentTime + t);
      g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + t + 0.02); g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t + 0.18);
      o.connect(g); g.connect(ctx.destination); o.start(ctx.currentTime + t); o.stop(ctx.currentTime + t + 0.2);
    });
  } catch { /* noop */ }
}

const secsLeft = (iso) => Math.max(0, Math.floor((new Date(iso).getTime() - Date.now()) / 1000));

// Aviso falado (WCAG 2.2.1 / 4.1.3): anuncia novos chamados e marcos do tempo, sem repetir a cada segundo.
function useAnnouncer(invites) {
  const [msg, setMsg] = useState("");
  const said = useRef(new Set());
  useEffect(() => {
    invites.forEach((inv) => {
      const s = secsLeft(inv.deadline_at), k = inv.application_id;
      const say = (tag, text) => { if (!said.current.has(k + tag)) { said.current.add(k + tag); setMsg(text); } };
      say("new", `Novo chamado Em cima da Hora: ${inv.instrument}, hoje às ${inv.start_time}, cachê ${money(inv.cache)}. Você tem ${s} segundos para responder.`);
      if (s <= 30 && s > 10) say("30", `Restam 30 segundos para responder ao chamado de ${inv.instrument}.`);
      if (s <= 10 && s > 0) say("10", `Restam 10 segundos para responder ao chamado de ${inv.instrument}.`);
      if (s === 0) say("0", `Tempo esgotado para o chamado de ${inv.instrument}.`);
    });
  });
  return msg;
}

function fmtLeft(iso) {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "00:00";
  const s = Math.floor(ms / 1000);
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

// Chamados ⚡ Em cima da Hora endereçados ao músico (visão do freelancer, com contador de resposta).
export default function ExpressInvites() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMusician = user?.user_type === "freelancer" || user?.user_type === "both";
  const [invites, setInvites] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [busy, setBusy] = useState(null);
  const [muted, setMuted] = useState(localStorage.getItem(MUTE_KEY) === "1");
  const [, setTick] = useState(0);
  const known = useRef(null);

  const load = useCallback(() => {
    if (!isMusician) return;
    api.get("/express/invites").then((r) => {
      const ids = r.data.map((x) => x.application_id);
      if (known.current && ids.some((id) => !known.current.has(id))) alertNewCall();
      known.current = new Set(ids);
      setInvites(r.data);
    }).catch(() => {});
    api.get("/express/my-assignments").then((r) => setJobs(r.data)).catch(() => {});
  }, [isMusician]);
  useLiveRefresh(load, { match: (m) => m.type.startsWith("urgent_"), slow: 30000, fast: 5000 });
  useEffect(() => { if (!isMusician) return; const t = setInterval(() => setTick((x) => x + 1), 1000); return () => clearInterval(t); }, [isMusician]);
  const toggleMute = () => { const v = !muted; setMuted(v); localStorage.setItem(MUTE_KEY, v ? "1" : "0"); };
  useEffect(() => { window.dispatchEvent(new CustomEvent("express-invites-pending", { detail: invites.length > 0 })); }, [invites.length]);

  const respond = async (inv, accept) => {
    setBusy(inv.application_id);
    try {
      await api.post(`/applications/${inv.application_id}/invite-response`, { accept });
      toast[accept ? "success" : "info"](accept ? "⚡ Vaga reservada! Confirmação do servidor recebida." : "Convite recusado.");
      setInvites((p) => p.filter((x) => x.application_id !== inv.application_id));
      if (accept) navigate(`/em-cima-da-hora/servico/${inv.application_id}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); load(); }
    finally { setBusy(null); }
  };

  const announce = useAnnouncer(invites);
  const live = <div role="status" aria-live="assertive" aria-atomic="true" className="sr-only" data-testid="express-invites-announcer">{announce}</div>;
  const jobBar = jobs.length > 0 && (
    <div className="mb-4 space-y-2" data-testid="express-my-jobs">
      {jobs.map((j) => (
        <button key={j.application_id} type="button" onClick={() => navigate(`/em-cima-da-hora/servico/${j.application_id}`)} data-testid={`express-job-${j.application_id}`}
          className="w-full min-h-[44px] rounded-xl border border-[#FF4D00]/40 bg-[#FF4D00]/10 px-4 py-2 text-left text-sm flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF8A3D]">
          <Zap className="h-4 w-4 text-[#FF4D00]" aria-hidden="true" /><span className="flex-1">Serviço ⚡ {j.instrument} às {j.start_time} — toque para acompanhar</span><Navigation className="h-4 w-4" aria-hidden="true" />
        </button>
      ))}
    </div>
  );
  if (!invites.length) return <>{live}{isMusician && <PushOptIn />}{jobBar}</>;
  return (
    <section className="relative z-[110] space-y-3 mb-6" data-testid="express-invites" aria-labelledby="express-invites-title">
      {live}<PushOptIn />{jobBar}
      <div className="flex items-center justify-between">
        <h2 id="express-invites-title" className="font-head text-lg font-bold flex items-center gap-2"><Zap className="h-5 w-5 text-[#FF4D00]" aria-hidden="true" />Nova oportunidade em sua região!</h2>
        <button type="button" onClick={toggleMute} aria-pressed={muted} aria-label={muted ? "Ativar som dos chamados" : "Silenciar som dos chamados"} data-testid="express-invites-mute" className="h-11 w-11 rounded-full flex items-center justify-center hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF8A3D]">
          {muted ? <VolumeX className="h-5 w-5 text-zinc-400" aria-hidden="true" /> : <Volume2 className="h-5 w-5 text-[#FF8A3D]" aria-hidden="true" />}
        </button>
      </div>
      {invites.map((inv) => {
        const left = fmtLeft(inv.deadline_at);
        const expired = left === "00:00";
        const titleId = `express-inv-title-${inv.application_id}`;
        return (
          <article key={inv.application_id} aria-labelledby={titleId} data-testid={`express-invite-${inv.application_id}`} className="rounded-2xl border border-[#FF4D00]/40 bg-[#FF4D00]/[0.06] p-4 space-y-3 shadow-[0_0_30px_rgba(255,77,0,0.12)]">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-[0.16em] text-[#FF8A3D] font-bold flex items-center gap-1"><Zap className="h-3 w-3" aria-hidden="true" />Chamado em cima da hora</div>
                <h3 id={titleId} className="font-head font-bold text-lg mt-0.5">{inv.event_name.replace("⚡ Em cima da Hora — ", "")}</h3>
                <div className="text-sm text-zinc-300">{inv.instrument} · Hoje · {inv.start_time}–{inv.end_time}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-xs text-zinc-300" aria-hidden="true">Responda em</div>
                <div role="timer" aria-live="off" aria-label={`Tempo restante para responder: ${secsLeft(inv.deadline_at)} segundos`} data-testid={`express-invite-timer-${inv.application_id}`} className={`font-head text-2xl font-extrabold tabular-nums ${expired ? "text-rose-400" : "text-[#FF8A3D]"}`}>{left}</div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-300">
              {inv.area && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{inv.area}</span>}
              {inv.distance_km != null && <span>{inv.distance_km} km</span>}
              {inv.eta_min != null && <span className="inline-flex items-center gap-1 text-[#FF4D00]"><Navigation className="h-3.5 w-3.5" />ETA {inv.eta_min} min</span>}
            </div>
            <div className="flex items-center justify-between rounded-xl bg-black/20 p-2.5">
              <div className="flex items-center gap-2 min-w-0">
                <div className="h-8 w-8 rounded-full bg-[#1C1C24] overflow-hidden flex items-center justify-center text-sm font-bold shrink-0">{inv.creator.photo ? <img src={inv.creator.photo} alt="" aria-hidden="true" className="h-full w-full object-cover" /> : (inv.creator.stage_name?.[0] || "?")}</div>
                <div className="min-w-0"><div className="text-sm font-medium truncate">{inv.creator.stage_name || "Organizador"}</div>
                  <div className="text-xs text-zinc-300 flex items-center gap-2">{inv.creator.rating > 0 && <span className="inline-flex items-center gap-0.5"><Star className="h-3 w-3 text-amber-400 fill-amber-400" aria-hidden="true" /><span className="sr-only">Avaliação</span>{inv.creator.rating}</span>}<span>{inv.creator.gigs} eventos</span></div></div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-head font-bold text-[#FF4D00]">{money(inv.cache)}</div>
                {inv.protected && <div className="text-xs text-emerald-400 inline-flex items-center gap-0.5"><ShieldCheck className="h-3 w-3" aria-hidden="true" />Cachê protegido</div>}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Button data-testid={`express-invite-decline-${inv.application_id}`} aria-label={`Recusar chamado de ${inv.instrument}`} disabled={busy === inv.application_id} onClick={() => respond(inv, false)} className="h-11 rounded-xl bg-rose-700 hover:bg-rose-800 text-white font-semibold">Recusar</Button>
              <Button data-testid={`express-invite-details-${inv.application_id}`} variant="outline" onClick={() => navigate(`/em-cima-da-hora/oferta/${inv.application_id}`)} className="h-11 rounded-xl bg-transparent border-white/20">Ver detalhes</Button>
              <Button data-testid={`express-invite-accept-${inv.application_id}`} aria-label={`Aceitar chamado de ${inv.instrument} por ${money(inv.cache)}`} disabled={busy === inv.application_id || expired} onClick={() => respond(inv, true)} className="h-11 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold gap-1"><Zap className="h-4 w-4" aria-hidden="true" />Aceitar</Button>
            </div>
            <div className="h-1.5 rounded-full bg-white/10 overflow-hidden" aria-hidden="true"><div className="h-full bg-[#FF4D00] transition-[width] duration-1000 ease-linear" style={{ width: `${Math.min(100, (secsLeft(inv.deadline_at) / (inv.response_seconds || 30)) * 100)}%` }} /></div>
            {expired && <p className="text-xs text-rose-400 flex items-center gap-1"><Clock className="h-3 w-3" aria-hidden="true" />Tempo esgotado — a vaga pode já ter sido oferecida a outro músico.</p>}
          </article>
        );
      })}
    </section>
  );
}
